import { useEffect, useMemo, useRef, useState } from "react";
import UiIcon from "../components/UiIcon";
import { formatNumber } from "./copy";
import { localizePeriodLabel } from "../i18n/period-labels";
import { withPercent } from "../i18n/number-format";
import { INDICATOR_BANDS, MIN_COMPARABLE_RESULTS, indicatorStatus, subtreeIds, summarizePeople } from "./executive-insights";

// Interactive organization structure for the executive dashboard.
// Every value comes from the same executive payload as the rest of the page: a unit's CVI,
// Individual and Collective values are the mean of the per-person server results of the people
// in that unit and its sub-units (missing results are counted, never averaged as zero), and
// indicator achievement is the CVI engine's value.

const fmt = (value, language, digits = 1) => (value == null ? "—" : formatNumber(value, language, { minimumFractionDigits: digits, maximumFractionDigits: digits }));

export function buildOrgTree({ orgUnits = [], people = [], organizationName = "" }) {
    const active = orgUnits.filter(unit => unit.isActive !== false);
    const byParent = new Map();
    for (const unit of active) {
        const key = unit.parentId && active.some(other => other.id === unit.parentId) ? unit.parentId : "__root__";
        if (!byParent.has(key)) byParent.set(key, []);
        byParent.get(key).push(unit);
    }
    const build = unit => {
        const ids = subtreeIds(active, unit.id);
        const members = people.filter(person => ids.has(person?.user?.orgUnitId));
        return {
            id: unit.id, label: unit.label || unit.name, type: unit.type || "", managerUserId: unit.managerUserId || null,
            ids, members, summary: summarizePeople(members),
            children: (byParent.get(unit.id) || []).sort((a, b) => String(a.label || a.name).localeCompare(String(b.label || b.name))).map(build),
        };
    };
    const tops = (byParent.get("__root__") || []).sort((a, b) => String(a.label || a.name).localeCompare(String(b.label || b.name))).map(build);
    return { id: "__organization__", label: organizationName, type: "organization", root: true, members: people, summary: summarizePeople(people), children: tops };
}

function findNode(node, id) {
    if (node.id === id) return node;
    for (const child of node.children || []) {
        const found = findNode(child, id);
        if (found) return found;
    }
    return null;
}

function OrgNode({ node, selectedId, onSelect, language, s }) {
    const selected = node.id === selectedId;
    const value = node.summary?.cvi;
    return <li className="cvi-exec-org-item">
        <button type="button" className={`cvi-exec-org-node${selected ? " is-selected" : ""}${node.root ? " is-root" : ""}`}
            aria-pressed={selected} onClick={() => onSelect(node.id)}
            aria-label={s.nodeLabel(node.label, s.types[node.type] || node.type, node.summary?.total ?? 0, fmt(value, language))}>
            <span className="cvi-exec-org-node-name">{node.label}</span>
            <span className="cvi-exec-org-node-meta">{s.types[node.type] || node.type || s.unit} · {s.people(node.summary?.total ?? 0)}</span>
            <span className={`cvi-exec-org-node-score${value == null ? " is-empty" : ""}`}><small>CVI</small>{fmt(value, language)}</span>
        </button>
        {node.children?.length > 0 && <ul className="cvi-exec-org-children">
            {node.children.map(child => <OrgNode key={child.id} node={child} selectedId={selectedId} onSelect={onSelect} language={language} s={s} />)}
        </ul>}
    </li>;
}

function Metric({ label, value, help }) {
    return <div className="cvi-exec-org-metric"><span>{label}</span><strong>{value}</strong>{help && <small>{help}</small>}</div>;
}

export default function ExecutiveOrgStructure({ c, language, orgUnits = [], people = [], users = [], indicators = [], organizationName = "", period = null, state = "provisional", loading = false, onOpenUnit }) {
    const s = c.structure;
    const tree = useMemo(() => buildOrgTree({ orgUnits, people, organizationName: organizationName || s.organization }), [orgUnits, people, organizationName, s.organization]);
    const [selectedId, setSelectedId] = useState(tree.id);
    const chartRef = useRef(null);
    // A chart wider than its container opens centred on the organization, not on its left edge.
    const unitCount = orgUnits.length;
    useEffect(() => {
        const chart = chartRef.current;
        if (chart && chart.scrollWidth > chart.clientWidth) chart.scrollLeft = (chart.scrollWidth - chart.clientWidth) / 2;
    }, [unitCount]);
    const node = findNode(tree, selectedId) || tree;
    const manager = node.managerUserId ? users.find(user => user.id === node.managerUserId) : null;
    const unitIndicators = node.root
        ? indicators.filter(item => item.scope === "organization")
        : indicators.filter(item => item.orgUnitId && node.ids?.has(item.orgUnitId));
    const counts = node.summary?.counts || {};
    const small = (node.summary?.withCvi ?? 0) > 0 && node.summary.withCvi < MIN_COMPARABLE_RESULTS;
    if (!orgUnits.some(unit => unit.isActive !== false)) return null;

    return <section className="cvi-exec-card cvi-exec-org" aria-labelledby="exec-org-title" id="exec-org">
        <header className="cvi-exec-card-head">
            <div>
                <h2 id="exec-org-title">{s.title}</h2>
                <p>{s.subtitle}</p>
            </div>
        </header>
        <div className="cvi-exec-org-layout">
            <div ref={chartRef} className="cvi-exec-org-chart" role="group" aria-label={s.title} tabIndex={0}>
                <ul className="cvi-exec-org-tree">
                    <OrgNode node={tree} selectedId={node.id} onSelect={setSelectedId} language={language} s={s} />
                </ul>
            </div>
            <aside className="cvi-exec-org-panel" aria-live="polite" aria-label={s.panelLabel(node.label)}>
                <div className="cvi-exec-org-panel-head">
                    <span className="cvi-exec-org-panel-icon" aria-hidden="true"><UiIcon name={node.root ? "company" : "org"} size={17} /></span>
                    <div>
                        <strong>{node.label}</strong>
                        <small>{s.types[node.type] || node.type || s.unit}{period ? ` · ${localizePeriodLabel(period.label || period.name || "", language)} · ${c.periodStates[state] || state}` : ""}</small>
                    </div>
                </div>
                {loading ? <div className="cvi-exec-skeleton" aria-hidden="true"><span style={{ width: "90%" }} /><span style={{ width: "70%" }} /></div> : <>
                    <dl className="cvi-exec-org-facts">
                        <div><dt>{s.manager}</dt><dd>{node.root ? "—" : manager?.name || s.noManager}</dd></div>
                        <div><dt>{s.members}</dt><dd>{node.summary?.total ?? 0}</dd></div>
                    </dl>
                    <div className="cvi-exec-org-metrics">
                        <Metric label="CVI" value={fmt(node.summary?.cvi, language)} help={s.basis(node.summary?.withCvi ?? 0, node.summary?.total ?? 0)} />
                        <Metric label={c.hero.individual} value={fmt(node.summary?.individual, language)} help={`n = ${node.summary?.individualCount ?? 0}`} />
                        <Metric label={c.hero.collective} value={fmt(node.summary?.collective, language)} help={`n = ${node.summary?.collectiveCount ?? 0}`} />
                    </div>
                    {small && <p className="cvi-exec-org-note">{s.smallSample}</p>}
                    <div className="cvi-exec-org-section">
                        <span className="cvi-exec-org-section-label">{s.coverage}</span>
                        <ul className="cvi-exec-org-coverage">
                            {["final", "provisional", "partial", "not_assessed"].filter(key => counts[key]).map(key =>
                                <li key={key} className={`is-${key}`}><i aria-hidden="true" />{c.coverage.statuses?.[key] || key}<strong>{counts[key]}</strong></li>)}
                            {!node.summary?.total && <li className="is-not_assessed"><i aria-hidden="true" />{s.noPeople}</li>}
                        </ul>
                    </div>
                    <div className="cvi-exec-org-section">
                        <span className="cvi-exec-org-section-label">{s.indicators}</span>
                        {unitIndicators.length === 0 ? <p className="cvi-exec-muted">{s.noIndicators}</p> : <ul className="cvi-exec-org-indicators">
                            {unitIndicators.slice(0, 6).map(item => {
                                const status = indicatorStatus(item);
                                return <li key={item.indicatorId}>
                                    <span>{item.name}</span>
                                    <strong className={`is-${status}`}>{item.achievement == null ? s.noData : withPercent(fmt(item.achievement, language, 0), language)}</strong>
                                </li>;
                            })}
                            {unitIndicators.length > 6 && <li className="cvi-exec-muted">{s.more(unitIndicators.length - 6)}</li>}
                        </ul>}
                        <small className="cvi-exec-muted">{s.indicatorBands(INDICATOR_BANDS.watch, INDICATOR_BANDS.onTrack)}</small>
                    </div>
                    {!node.root && onOpenUnit && <button type="button" className="cvi-exec-button is-primary cvi-exec-org-open" onClick={() => onOpenUnit(node.id)}>
                        {s.viewDetails}<UiIcon name="chevronRight" size={14} />
                    </button>}
                    {!node.root && onOpenUnit && <small className="cvi-exec-muted">{s.detailsNote}</small>}
                </>}
            </aside>
        </div>
    </section>;
}
